import React, { useState, useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { METRICS } from '../config/business';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck } = FiIcons;

function Counter({ value, suffix, duration = 2 }) {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      setCount(value);
      return;
    }

    let start = 0;
    const end = parseInt(value);
    if (start === end) return;

    let totalMiliseconds = duration * 1000;
    let incrementTime = totalMiliseconds / end;

    let timer = setInterval(() => {
      start += 1;
      setCount(start);
      if (start === end) clearInterval(timer);
    }, incrementTime);

    return () => clearInterval(timer);
  }, [value, duration, prefersReducedMotion]);

  return <span>{count}{suffix}</span>;
}

function MetricsStrip() {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // Keep the metric strip functional in browsers that do not support this API.
    if (typeof window.IntersectionObserver !== 'function') {
      setIsVisible(true);
      return undefined;
    }

    let observer;
    try {
      observer = new window.IntersectionObserver(
        ([entry]) => {
          if (entry?.isIntersecting) {
            setIsVisible(true);
            observer?.unobserve(entry.target);
          }
        },
        { threshold: 0.1 }
      );

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }
    } catch (error) {
      console.warn('Metric animation observer could not be started; showing metrics immediately.', error);
      setIsVisible(true);
    }

    return () => observer?.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="bg-slate-900 py-12 sm:py-16 lg:py-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
          {METRICS.map((metric, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.2 }}
              className="text-center md:text-left flex flex-col items-center md:items-start"
            >
              <div className="text-5xl sm:text-6xl font-bold text-white mb-3 font-mono">
                {isVisible ? (
                  <Counter value={metric.value} suffix={metric.suffix} />
                ) : (
                  <span>0{metric.suffix}</span>
                )}
              </div>
              <div className="text-blue-400 font-bold text-sm uppercase tracking-widest mb-4">
                {metric.label}
              </div>
              <ul className="flex flex-wrap justify-center md:justify-start gap-x-4 gap-y-2 max-w-xs md:max-w-none">
                {metric.bullets.map((bullet, i) => (
                  <li key={i} className="flex items-center text-slate-400 text-xs">
                    <SafeIcon icon={FiCheck} className="h-3.5 w-3.5 text-blue-500 mr-2 shrink-0" />
                    {bullet}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default MetricsStrip;